import { useState, useEffect, FormEvent, ChangeEvent } from 'react'
import { Spinner } from '@/components/ui/spinner'
import { useNavigate, useLocation } from 'react-router-dom'
import { TextLink } from '@/components/ui/text-link'
import { AuthPageFrame } from './AuthPageFrame'
import {
  registerWithMinimalData,
  signInWithGoogle,
  signInWithApple,
} from '../../services/auth'
import { useSession } from '../../contexts/SessionContext'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Icon } from '@/components/ui/icon'
import {
  faUserPlus,
  faCircleExclamation,
} from '@awesome.me/kit-e215ba4646/icons/sharp/light'
import type { UserRole } from '@/constants/userRoles'
import { UserRoles } from '../../services/auth'
import { requestAccess } from '@/services/auth/requestAccess'
import { InviteOnlyMessages } from '@/constants/messages'
import { APPLE_SIGN_IN_ENABLED } from '@/constants/authProviders'

interface InviteData {
  email?: string
  firstName?: string
  lastName?: string
  phoneNumber?: string
  role?: string
  createdBy?: string
}

interface SignUpProps {
  redirectPath?: string
}

const SignUp = ({ redirectPath = '/billing' }: SignUpProps) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [inviteData, setInviteData] = useState<InviteData | null>(null)
  const [requestFirstName, setRequestFirstName] = useState('')
  const [requestLastName, setRequestLastName] = useState('')
  // A landlord arriving from a tenant's invite email (E15, PB-64) gets their
  // address filled in, and the request says where it came from.
  const landlordParams = new URLSearchParams(window.location.search)
  const fromTenantInvite = landlordParams.get('ref') === 'tenant'
  const [requestEmail, setRequestEmail] = useState(
    fromTenantInvite ? landlordParams.get('email') || '' : ''
  )
  const [invitationRequested, setInvitationRequested] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated } = useSession()

  // Invite-only sign-up (UAT + the prod beta, PB-D14): the page shows a
  // request-invitation form instead of open sign-up
  const isInviteOnly = import.meta.env.VITE_INVITE_ONLY_SIGNUP === 'true'

  // Parse invite token from URL if present
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const token = params.get('token')

    if (token) {
      try {
        // Decode the token (base64)
        const decodedToken: InviteData = JSON.parse(atob(token))
        setInviteData(decodedToken)

        // Pre-fill fields if provided in token
        if (decodedToken.email) {
          setEmail(decodedToken.email)
        }
        if (decodedToken.firstName) {
          setFirstName(decodedToken.firstName)
        }
        if (decodedToken.lastName) {
          setLastName(decodedToken.lastName)
        }
        if (decodedToken.phoneNumber) {
          setPhoneNumber(decodedToken.phoneNumber)
        }
      } catch (err) {
        console.error('Invalid invite token:', err)
        setError('Invalid invitation link. Please contact support.')
      }
    }
  }, [location])

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectPath)
    }
  }, [isAuthenticated, navigate, redirectPath])

  const validateForm = (): boolean => {
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return false
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters')
      return false
    }

    return true
  }

  const handleEmailSignUp = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!validateForm()) return

    // Validate required fields for minimal registration
    if (!firstName.trim()) {
      setError('First name is required')
      return
    }

    if (!lastName.trim()) {
      setError('Last name is required')
      return
    }

    setError('')
    setLoading(true)

    try {
      // Determine role based on invitation or default for self-registration
      let role: UserRole
      let createdBy: string | null

      if (inviteData) {
        // Use role from invitation
        role = (inviteData.role as UserRole) || UserRoles.ADMIN
        createdBy = inviteData.createdBy || null
      } else {
        // Default role for self-registration - account type/billing handled later
        role = UserRoles.ADMIN
        createdBy = null // Self-registered users have no creator
      }

      // Use enhanced minimal registration
      const result = await registerWithMinimalData(
        {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          password: password,
          phoneNumber: phoneNumber.trim() || undefined,
          role: role,
        },
        createdBy
      )

      if (result.success) {
        // Store invite data for profile completion if available
        if (inviteData) {
          sessionStorage.setItem('inviteData', JSON.stringify(inviteData))
        }

        navigate(redirectPath)
      } else {
        setError(result.error || 'Failed to create account')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError('Failed to create account: ' + message)
    }

    setLoading(false)
  }

  const handleGoogleSignUp = async () => {
    setError('')
    setLoading(true)

    try {
      const result = await signInWithGoogle()

      if (result.success) {
        // Store invite data to be used in profile completion
        if (inviteData) {
          sessionStorage.setItem(
            'userProfile',
            JSON.stringify({
              inviteData,
            })
          )
        }

        navigate(redirectPath)
      } else {
        setError(result.error || 'Failed to sign up with Google')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError('Failed to sign up with Google: ' + message)
    }

    setLoading(false)
  }

  const handleAppleSignUp = async () => {
    setError('')
    setLoading(true)

    try {
      const result = await signInWithApple()

      if (result.success) {
        // Store invite data to be used in profile completion
        if (inviteData) {
          sessionStorage.setItem(
            'userProfile',
            JSON.stringify({
              inviteData,
            })
          )
        }

        navigate(redirectPath)
      } else {
        setError(result.error || 'Failed to sign up with Apple')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError('Failed to sign up with Apple: ' + message)
    }

    setLoading(false)
  }

  const handleInvitationRequest = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    const result = await requestAccess({
      firstName: requestFirstName,
      lastName: requestLastName,
      email: requestEmail,
      source: fromTenantInvite ? 'tenant-invite' : 'uat-signup-page',
    })
    if (result === 'sent') setInvitationRequested(true)
    else if (result === 'too-many') setError(InviteOnlyMessages.requestTooMany)
    else setError(InviteOnlyMessages.requestFailed)
    setLoading(false)
  }

  return (
    <AuthPageFrame>
      <div className="flex flex-col space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">
          {inviteData ? 'Complete Your Registration' : 'Create Account'}
        </h1>
        <p className="text-sm text-muted-foreground">
          {inviteData
            ? 'You have been invited to join MNTND'
            : 'Join MNTND to manage your properties'}
        </p>

        {inviteData && inviteData.role && (
          <Badge variant="secondary" className="mx-auto mt-2">
            <Icon icon={faUserPlus} className="w-3 h-3 mr-1" />
            Invited as {inviteData.role.toUpperCase()}
          </Badge>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <Icon icon={faCircleExclamation} className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Invite-only: request invitation form */}
      {isInviteOnly && !inviteData ? (
        <Card>
          <CardHeader>
            <div className="text-center space-y-2">
              <Icon
                icon={faCircleExclamation}
                className="h-12 w-12 text-warning mx-auto"
              />
              <h3 className="text-lg font-semibold">Invitation Required</h3>
              <p className="text-sm text-muted-foreground">
                MNTND is invitation-only for now. Request an invitation below
                and we&apos;ll email you once you&apos;re approved.
              </p>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleInvitationRequest} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="request-firstName">First Name</Label>
                  <Input
                    id="request-firstName"
                    autoComplete="given-name"
                    placeholder="John"
                    type="text"
                    autoCapitalize="words"
                    value={requestFirstName}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setRequestFirstName(e.target.value)
                    }
                    required
                    disabled={invitationRequested}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="request-lastName">Last Name</Label>
                  <Input
                    id="request-lastName"
                    autoComplete="family-name"
                    placeholder="Doe"
                    type="text"
                    autoCapitalize="words"
                    value={requestLastName}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setRequestLastName(e.target.value)
                    }
                    required
                    disabled={invitationRequested}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="request-email">Email</Label>
                <Input
                  id="request-email"
                  placeholder="name@example.com"
                  type="email"
                  autoCapitalize="none"
                  autoComplete="email"
                  value={requestEmail}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setRequestEmail(e.target.value)
                  }
                  required
                  disabled={invitationRequested}
                />
              </div>
              {invitationRequested ? (
                <>
                  <Alert>
                    <Icon icon={faCircleExclamation} className="h-4 w-4" />
                    <AlertTitle>Request Submitted</AlertTitle>
                    <AlertDescription>
                      Your invitation request has been submitted. You&apos;ll
                      receive an email when approved.
                    </AlertDescription>
                  </Alert>
                </>
              ) : (
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Submitting...' : 'Request Invitation'}
                </Button>
              )}
            </form>
            <div className="mt-4 text-center">
              <TextLink to="/signin" className="text-sm">
                Already have an account? Sign In
              </TextLink>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-6">
            <form onSubmit={handleEmailSignUp}>
              <div className="grid gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-1">
                    <Label className="sr-only" htmlFor="firstName">
                      First Name
                    </Label>
                    <Input
                      id="firstName"
                      placeholder="First name"
                      type="text"
                      autoCapitalize="words"
                      autoComplete="given-name"
                      disabled={
                        loading || !!(inviteData && inviteData.firstName)
                      }
                      value={firstName}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setFirstName(e.target.value)
                      }
                      required
                    />
                  </div>
                  <div className="grid gap-1">
                    <Label className="sr-only" htmlFor="lastName">
                      Last Name
                    </Label>
                    <Input
                      id="lastName"
                      placeholder="Last name"
                      type="text"
                      autoCapitalize="words"
                      autoComplete="family-name"
                      disabled={
                        loading || !!(inviteData && inviteData.lastName)
                      }
                      value={lastName}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setLastName(e.target.value)
                      }
                      required
                    />
                  </div>
                </div>

                <div className="grid gap-1">
                  <Label className="sr-only" htmlFor="email">
                    Email
                  </Label>
                  <Input
                    id="email"
                    placeholder="name@example.com"
                    type="email"
                    autoCapitalize="none"
                    autoComplete="email"
                    autoCorrect="off"
                    disabled={loading || !!(inviteData && inviteData.email)}
                    value={email}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setEmail(e.target.value)
                    }
                    required
                  />
                </div>

                {inviteData && inviteData.phoneNumber && (
                  <div className="grid gap-1">
                    <Label className="sr-only" htmlFor="phoneNumber">
                      Phone Number
                    </Label>
                    <Input
                      id="phoneNumber"
                      placeholder="+1 (555) 123-4567"
                      type="tel"
                      autoComplete="tel"
                      disabled={true}
                      value={phoneNumber}
                      readOnly
                    />
                  </div>
                )}

                <div className="grid gap-1">
                  <Label className="sr-only" htmlFor="password">
                    Password
                  </Label>
                  <Input
                    id="password"
                    placeholder="Password"
                    type="password"
                    autoCapitalize="none"
                    autoComplete="new-password"
                    disabled={loading}
                    value={password}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setPassword(e.target.value)
                    }
                    required
                  />
                </div>

                <div className="grid gap-1">
                  <Label className="sr-only" htmlFor="confirmPassword">
                    Confirm Password
                  </Label>
                  <Input
                    id="confirmPassword"
                    placeholder="Confirm password"
                    type="password"
                    autoCapitalize="none"
                    autoComplete="new-password"
                    disabled={loading}
                    value={confirmPassword}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setConfirmPassword(e.target.value)
                    }
                    required
                  />
                </div>

                <Button type="submit" disabled={loading}>
                  {loading && <Spinner className="mr-2" />}
                  Create Account
                </Button>
              </div>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Or continue with
                </span>
              </div>
            </div>

            <div className="grid gap-2">
              <Button
                variant="outline"
                type="button"
                disabled={loading}
                onClick={handleGoogleSignUp}
              >
                {loading ? (
                  <Spinner className="mr-2" />
                ) : (
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                )}
                Google
              </Button>

              {APPLE_SIGN_IN_ENABLED && (
                <Button
                  variant="outline"
                  type="button"
                  disabled={loading}
                  onClick={handleAppleSignUp}
                >
                  {loading ? (
                    <Spinner className="mr-2" />
                  ) : (
                    <svg
                      className="mr-2 h-4 w-4"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                    </svg>
                  )}
                  Apple
                </Button>
              )}
            </div>
          </div>

          <p className="px-8 text-center text-sm">
            <TextLink to="/signin">Already have an account? Sign In</TextLink>
          </p>
        </>
      )}
    </AuthPageFrame>
  )
}

export default SignUp
